import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Grid,
  Divider,
  Chip,
  Pagination,
  Stack,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";

/*
public class BulkTagMappingResultsDTO {
    public string TagName { get; set; }
    public int TagsUpdated { get; set; }
    public int DuplicatesFound { get; set; }
    public int Errors { get; set; }
    public int TagsSkipped { get; set; }
    public List<string> ErrorMessages { get; set; } = new List<string>();
    public List<string> AssetsWithDuplicates { get; set; } = new List<string>();
    public List<string> AssetsWhereUDAlreadyExists { get; set; } = new List<string>();
}
*/

const BulkTagMappingResults = ({ results }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (!results || !Array.isArray(results) || results.length === 0) {
    return null;
  }

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Get current result based on page
  const currentResult = results[currentPage - 1];
  const {
    TagName,
    TagsUpdated,
    DuplicatesFound,
    Errors,
    ErrorMessages,
    AssetsWithDuplicates,
    TagsSkipped,
    AssetsWhereUDAlreadyExists,
  } = currentResult;
  const hasErrors = Errors > 0;
  const hasDuplicates = DuplicatesFound > 0;
  const hasAssetsWithDuplicates =
    AssetsWithDuplicates && AssetsWithDuplicates.length > 0;
  const hasTagsSkipped = TagsSkipped > 0;
  const hasAssetsWhereUDAlreadyExists =
    AssetsWhereUDAlreadyExists && AssetsWhereUDAlreadyExists.length > 0;
  const hasSuccess = TagsUpdated > 0;

  // Determine overall status
  const getOverallStatus = () => {
    if (hasErrors && !hasSuccess) {
      return {
        severity: "error",
        title: "Operation Failed",
        icon: <ErrorIcon />,
      };
    }
    if (hasErrors && hasSuccess) {
      return {
        severity: "warning",
        title: "Completed with Errors",
        icon: <WarningIcon />,
      };
    }
    if (hasDuplicates) {
      return {
        severity: "warning",
        title: "Completed with Warnings",
        icon: <WarningIcon />,
      };
    }
    return {
      severity: "success",
      title: "Operation Successful",
      icon: <CheckCircleIcon />,
    };
  };

  const status = getOverallStatus();

  // Calculate overall statistics
  const totalStats = results.reduce(
    (acc, result) => ({
      tagsUpdated: acc.tagsUpdated + result.TagsUpdated,
      duplicatesFound: acc.duplicatesFound + result.DuplicatesFound,
      errors: acc.errors + result.Errors,
    }),
    { tagsUpdated: 0, duplicatesFound: 0, errors: 0 }
  );

  return (
    <Box sx={{ mt: 3 }}>
      {results.length > 1 && (
        <Card sx={{ mb: 2, bgcolor: "info.light" }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Overall Summary ({results.length} Operations)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant='body2' color='text.secondary'>
                  Total Tags Updated
                </Typography>
                <Typography variant='h5' sx={{ fontWeight: "bold" }}>
                  {totalStats.tagsUpdated}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant='body2' color='text.secondary'>
                  Total Duplicates
                </Typography>
                <Typography variant='h5' sx={{ fontWeight: "bold" }}>
                  {totalStats.duplicatesFound}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant='body2' color='text.secondary'>
                  Total Errors
                </Typography>
                <Typography variant='h5' sx={{ fontWeight: "bold" }}>
                  {totalStats.errors}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 2 }}>
        <Box>
          <Typography variant='h6'>
            {results.length > 1
              ? `Operation ${currentPage} of ${results.length}`
              : "Operation Result"}
          </Typography>
          {TagName && (
            <Typography
              variant='subtitle2'
              color='text.secondary'
              sx={{ mt: 0.5 }}>
              Tag Name: <strong>{TagName}</strong>
            </Typography>
          )}
        </Box>
        {results.length > 1 && (
          <Pagination
            count={results.length}
            page={currentPage}
            onChange={handlePageChange}
            color='primary'
            showFirstButton
            showLastButton
          />
        )}
      </Stack>

      <Alert severity={status.severity} icon={status.icon} sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontWeight: "bold" }}>{status.title}</AlertTitle>
        {hasSuccess &&
          `Successfully updated ${TagsUpdated} tag${TagsUpdated !== 1 ? "s" : ""}.`}
        {hasDuplicates &&
          ` ${DuplicatesFound} duplicate${DuplicatesFound !== 1 ? "s" : ""} found.`}
        {hasErrors && ` ${Errors} error${Errors !== 1 ? "s" : ""} occurred.`}
      </Alert>

      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom sx={{ mb: 3 }}>
            Operation Summary
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: hasSuccess ? "success.light" : "grey.100",
                  borderRadius: 1,
                }}>
                <CheckCircleIcon
                  sx={{
                    fontSize: 40,
                    color: hasSuccess ? "success.dark" : "grey.500",
                    mb: 1,
                  }}
                />
                <Typography
                  variant='h4'
                  sx={{ fontWeight: "bold", color: "text.primary" }}>
                  {TagsUpdated}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Tags Updated
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Tooltip title={`Number of duplicate tags named "${TagName}".`}>
                <Box
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: hasDuplicates ? "warning.light" : "grey.100",
                    borderRadius: 1,
                  }}>
                  <WarningIcon
                    sx={{
                      fontSize: 40,
                      color: hasDuplicates ? "warning.dark" : "grey.500",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant='h4'
                    sx={{ fontWeight: "bold", color: "text.primary" }}>
                    {DuplicatesFound}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Duplicates Found
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: hasTagsSkipped ? "warning.light" : "grey.100",
                  borderRadius: 1,
                }}>
                <WarningIcon
                  sx={{
                    fontSize: 40,
                    color: hasTagsSkipped ? "warning.dark" : "grey.500",
                    mb: 1,
                  }}
                />
                <Typography
                  variant='h4'
                  sx={{ fontWeight: "bold", color: "text.primary" }}>
                  {TagsSkipped}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Tags Skipped
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: hasErrors ? "error.light" : "grey.100",
                  borderRadius: 1,
                }}>
                <ErrorIcon
                  sx={{
                    fontSize: 40,
                    color: hasErrors ? "error.dark" : "grey.500",
                    mb: 1,
                  }}
                />
                <Typography
                  variant='h4'
                  sx={{ fontWeight: "bold", color: "text.primary" }}>
                  {Errors}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Errors
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {hasAssetsWhereUDAlreadyExists && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography
                  variant='h6'
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WarningIcon color='warning' />
                  Assets Where UD Already Exists
                </Typography>
                <List sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1 }}>
                  {AssetsWhereUDAlreadyExists.map((assetName, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: "background.paper",
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "warning.light",
                      }}>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}>
                            <Chip
                              label={`${assetName}`}
                              size='small'
                              color='warning'
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {hasAssetsWithDuplicates && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Tooltip
                  title={`These assets had more than one tag with the name "${TagName}".`}>
                  <Typography
                    variant='h6'
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningIcon color='warning' />
                    Assets with Duplicate Tag Names
                  </Typography>
                </Tooltip>
                <List sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1 }}>
                  {AssetsWithDuplicates.map((assetName, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: "background.paper",
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "warning.light",
                      }}>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}>
                            <Chip
                              label={`${assetName}`}
                              size='small'
                              color='warning'
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {ErrorMessages && ErrorMessages.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography
                  variant='h6'
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <InfoIcon color='error' />
                  Error Details
                </Typography>
                <List sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1 }}>
                  {ErrorMessages.map((message, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: "background.paper",
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "error.light",
                      }}>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}>
                            <Chip
                              label={`Error ${index + 1}`}
                              size='small'
                              color='error'
                            />
                            <Typography variant='body2'>{message}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BulkTagMappingResults;
